import React, { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import Page from "../../components/Page";
import {
  IconPencil,
  IconPlus,
  IconTrash,
  IconBed,
  IconQrcode,
  IconCategory,
  IconCalendarEvent,
  IconCopy,
  IconLink
} from "@tabler/icons-react";
import { iconStroke } from "../../config/config";
import { useStoreSettings } from "../../controllers/settings.controller";
import toast from "react-hot-toast";
import QRCode from "qrcode";
import { useTheme } from "../../contexts/ThemeContext";
import apiClient from "../../helpers/ApiClient";
import { getRoomQRMenuLink } from "../../helpers/QRMenuHelper";

export default function RoomsSettingsPage() {
  const { t } = useTranslation();
  const { theme } = useTheme();

  const [activeTab, setActiveTab] = useState("rooms"); // rooms, categories

  const [rooms, setRooms] = useState([]);
  const [roomTypes, setRoomTypes] = useState([]);
  const [loading, setLoading] = useState(true);

  // iCal states
  const [selectedRoomForICal, setSelectedRoomForICal] = useState(null);
  const [roomICalLinks, setRoomICalLinks] = useState([]);
  const [isICalLoading, setIsICalLoading] = useState(false);
  const icalChannelNameRef = useRef();
  const icalUrlRef = useRef();

  // Form refs for Room Type
  const roomTypeNameRef = useRef();
  const roomTypeRateRef = useRef();
  const roomTypeIdRef = useRef();
  const roomTypeNameUpdateRef = useRef();
  const roomTypeRateUpdateRef = useRef();

  // Form refs for Room
  const roomNameRef = useRef();
  const roomTypeIdSelectRef = useRef();
  const roomImage1Ref = useRef();
  const roomImage2Ref = useRef();
  const roomIdRef = useRef();
  const roomNameUpdateRef = useRef();
  const roomTypeIdUpdateSelectRef = useRef();
  const roomImage1UpdateRef = useRef();
  const roomImage2UpdateRef = useRef();

  const { data: storeSettings, isLoading: isLoadingStore } = useStoreSettings();

  const fetchData = async () => {
    setLoading(true);
    try {
      const [roomsRes, typesRes] = await Promise.all([
        apiClient.get("/rooms"),
        apiClient.get("/rooms/types"),
      ]);
      setRooms(roomsRes.data || []);
      setRoomTypes(typesRes.data || []);
    } catch (e) {
      toast.error("Failed to load room settings data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  if (isLoadingStore || loading) {
    return (
      <Page className="px-8 py-6 flex justify-center items-center h-[60vh]">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-restro-green"></div>
      </Page>
    );
  }

  const uniqueQRCode = storeSettings?.uniqueQRCode;
  const isQRMenuEnabled = storeSettings?.isQRMenuEnabled;
  const storeTenantId = storeSettings?.tenant_id; // Need this for the export URL

  // --- ROOM TYPE ACTIONS ---
  const handleAddRoomType = async (e) => {
    e.preventDefault();
    const name = roomTypeNameRef.current.value.trim();
    const rate = Number(roomTypeRateRef.current.value);

    if (!name || isNaN(rate)) {
      toast.error("Please provide valid name and rate.");
      return;
    }

    try {
      toast.loading("Creating room category...");
      const res = await apiClient.post("/rooms/types", { name, rate });
      if (res.status === 201 || res.status === 200) {
        toast.dismiss();
        toast.success(res.data.message || "Room category created!");
        document.getElementById("modal-add-type").close();
        roomTypeNameRef.current.value = "";
        roomTypeRateRef.current.value = "";
        fetchData();
      }
    } catch (err) {
      toast.dismiss();
      toast.error(err.response?.data?.message || "Failed to create category");
    }
  };

  const handleShowUpdateRoomType = (type) => {
    roomTypeIdRef.current.value = type.id;
    roomTypeNameUpdateRef.current.value = type.name;
    roomTypeRateUpdateRef.current.value = type.rate;
    document.getElementById("modal-update-type").showModal();
  };

  const handleUpdateRoomType = async (e) => {
    e.preventDefault();
    const id = roomTypeIdRef.current.value;
    const name = roomTypeNameUpdateRef.current.value.trim();
    const rate = Number(roomTypeRateUpdateRef.current.value);

    if (!name || isNaN(rate)) {
      toast.error("Please provide valid name and rate.");
      return;
    }

    try {
      toast.loading("Updating room category...");
      const res = await apiClient.put(`/rooms/types/${id}`, { name, rate });
      toast.dismiss();
      toast.success(res.data.message || "Room category updated!");
      document.getElementById("modal-update-type").close();
      fetchData();
    } catch (err) {
      toast.dismiss();
      toast.error(err.response?.data?.message || "Failed to update category");
    }
  };

  const handleDeleteRoomType = async (id) => {
    if (!window.confirm("Are you sure you want to delete this category? All rooms linked to it will need to be reassigned.")) return;
    try {
      toast.loading("Deleting room category...");
      await apiClient.delete(`/rooms/types/${id}`);
      toast.dismiss();
      toast.success("Room category deleted successfully!");
      fetchData();
    } catch (err) {
      toast.dismiss();
      toast.error(err.response?.data?.message || "Failed to delete category");
    }
  };

  // --- ROOM ACTIONS ---
  const handleAddRoom = async (e) => {
    e.preventDefault();
    const name = roomNameRef.current.value.trim();
    const room_type_id = roomTypeIdSelectRef.current.value;
    const image_1 = roomImage1Ref.current.value.trim() || null;
    const image_2 = roomImage2Ref.current.value.trim() || null;

    if (!name || !room_type_id) {
      toast.error("Please provide room number and select category.");
      return;
    }

    try {
      toast.loading("Adding room...");
      const res = await apiClient.post("/rooms", { name, room_type_id, image_1, image_2 });
      if (res.status === 201 || res.status === 200) {
        toast.dismiss();
        toast.success(res.data.message || "Room added!");
        document.getElementById("modal-add-room").close();
        roomNameRef.current.value = "";
        roomImage1Ref.current.value = "";
        roomImage2Ref.current.value = "";
        fetchData();
      }
    } catch (err) {
      toast.dismiss();
      toast.error(err.response?.data?.message || "Failed to add room");
    }
  };

  const handleShowUpdateRoom = (room) => {
    roomIdRef.current.value = room.id;
    roomNameUpdateRef.current.value = room.name;
    roomTypeIdUpdateSelectRef.current.value = room.room_type_id;
    roomImage1UpdateRef.current.value = room.image_1 || "";
    roomImage2UpdateRef.current.value = room.image_2 || "";
    document.getElementById("modal-update-room").showModal();
  };

  const handleUpdateRoom = async (e) => {
    e.preventDefault();
    const id = roomIdRef.current.value;
    const name = roomNameUpdateRef.current.value.trim();
    const room_type_id = roomTypeIdUpdateSelectRef.current.value;
    const image_1 = roomImage1UpdateRef.current.value.trim() || null;
    const image_2 = roomImage2UpdateRef.current.value.trim() || null;

    if (!name || !room_type_id) {
      toast.error("Please provide room number and select category.");
      return;
    }

    try {
      toast.loading("Updating room...");
      const res = await apiClient.put(`/rooms/${id}`, { name, room_type_id, image_1, image_2 });
      toast.dismiss();
      toast.success(res.data.message || "Room updated!");
      document.getElementById("modal-update-room").close();
      fetchData();
    } catch (err) {
      toast.dismiss();
      toast.error(err.response?.data?.message || "Failed to update room");
    }
  };

  const handleDeleteRoom = async (id) => {
    if (!window.confirm("Are you sure you want to delete this room?")) return;
    try {
      toast.loading("Deleting room...");
      await apiClient.delete(`/rooms/${id}`);
      toast.dismiss();
      toast.success("Room deleted successfully!");
      fetchData();
    } catch (err) {
      toast.dismiss();
      toast.error(err.response?.data?.message || "Failed to delete room");
    }
  };

  const handleDownloadRoomQR = async (roomName) => {
    try {
      if (!isQRMenuEnabled) {
        toast.error("Please enable QR Menu in the details settings first.");
        return;
      }

      // Generate a room-specific QR code URL
      const QR_MENU_LINK = getRoomQRMenuLink(uniqueQRCode, roomName);
      const qrDataURL = await QRCode.toDataURL(QR_MENU_LINK, { width: 1080 });
      const link = document.createElement("a");

      const fileName = roomName.replace(/[^a-z0-9]/gi, "_").toLowerCase();

      link.download = `room_${fileName}_menu_qr.png`;
      link.href = qrDataURL;
      link.click();
      link.remove();
      toast.success(`QR Menu Scanner for Room ${roomName} downloaded!`);
    } catch (error) {
      toast.error("Failed to generate room QR code.");
    }
  };

  // --- ICAL ACTIONS ---
  const handleOpenICalModal = async (room) => {
    setSelectedRoomForICal(room);
    setRoomICalLinks([]);
    document.getElementById("modal-ical").showModal();
    fetchICalLinks(room.id);
  };

  const fetchICalLinks = async (roomId) => {
    setIsICalLoading(true);
    try {
      const res = await apiClient.get(`/rooms/${roomId}/ical-links`);
      setRoomICalLinks(res.data || []);
    } catch (err) {
      toast.error("Failed to load iCal links");
    } finally {
      setIsICalLoading(false);
    }
  };

  const handleAddICalLink = async (e) => {
    e.preventDefault();
    const channelName = icalChannelNameRef.current.value.trim();
    const icalUrl = icalUrlRef.current.value.trim();

    if (!channelName || !icalUrl) return;

    try {
      toast.loading("Adding iCal Link...");
      await apiClient.post(`/rooms/${selectedRoomForICal.id}/ical-links`, {
        channel_name: channelName,
        ical_url: icalUrl
      });
      toast.dismiss();
      toast.success("iCal link added!");
      icalChannelNameRef.current.value = "";
      icalUrlRef.current.value = "";
      fetchICalLinks(selectedRoomForICal.id);
    } catch (err) {
      toast.dismiss();
      toast.error(err.response?.data?.message || "Failed to add link");
    }
  };

  const handleDeleteICalLink = async (linkId) => {
    if (!window.confirm("Remove this iCal sync link?")) return;
    try {
      toast.loading("Removing...");
      await apiClient.delete(`/rooms/ical-links/${linkId}`);
      toast.dismiss();
      toast.success("Removed successfully");
      fetchICalLinks(selectedRoomForICal.id);
    } catch (err) {
      toast.dismiss();
      toast.error("Failed to remove link");
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success("Export URL copied to clipboard!");
  };

  return (
    <Page className="px-8 py-6 text-restro-text dark:text-white">
      {/* Header */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-8">
        <div>
          <h1 className="text-3xl font-light tracking-tight text-gray-900 dark:text-white">Room Settings</h1>
          <p className="text-sm text-gray-500 mt-1">Configure room types, specific room numbers and generate printable QR ordering menus.</p>
        </div>
        <div className="flex gap-2">
          {activeTab === "rooms" ? (
            <button
              onClick={() => document.getElementById("modal-add-room").showModal()}
              className="border transition active:scale-95 hover:shadow-lg px-4 py-2.5 flex items-center gap-2 text-white bg-restro-green border-restro-green hover:bg-restro-green-button-hover rounded-xl"
            >
              <IconPlus size={18} stroke={iconStroke} /> Add Room Number
            </button>
          ) : (
            <button
              onClick={() => document.getElementById("modal-add-type").showModal()}
              className="border transition active:scale-95 hover:shadow-lg px-4 py-2.5 flex items-center gap-2 text-white bg-restro-green border-restro-green hover:bg-restro-green-button-hover rounded-xl"
            >
              <IconPlus size={18} stroke={iconStroke} /> Add Room Category
            </button>
          )}
        </div>
      </div>

      {/* Tabs */}
      <div className="flex border-b border-gray-100 dark:border-zinc-800 mb-8">
        <button
          onClick={() => setActiveTab("rooms")}
          className={`py-4 px-6 text-sm font-semibold border-b-2 -mb-px transition ${
            activeTab === "rooms"
              ? "border-restro-green text-restro-green"
              : "border-transparent text-gray-400 hover:text-gray-600"
          }`}
        >
          Rooms & QR Scanner
        </button>
        <button
          onClick={() => setActiveTab("categories")}
          className={`py-4 px-6 text-sm font-semibold border-b-2 -mb-px transition ${
            activeTab === "categories"
              ? "border-restro-green text-restro-green"
              : "border-transparent text-gray-400 hover:text-gray-600"
          }`}
        >
          Room Categories (Rates)
        </button>
      </div>

      {/* Content */}
      {activeTab === "rooms" ? (
        <div>
          {rooms.length === 0 ? (
            <div className="text-center py-20 bg-white dark:bg-zinc-900 border border-dashed rounded-3xl border-gray-200 dark:border-zinc-800">
              <IconBed size={48} className="mx-auto text-gray-300 mb-4" />
              <h3 className="text-lg font-semibold">No Rooms Configured</h3>
              <p className="text-sm text-gray-400 mt-1 mb-6">Create specific room numbers and link them to room categories.</p>
              <button
                onClick={() => document.getElementById("modal-add-room").showModal()}
                className="btn btn-outline border-restro-green text-restro-green hover:bg-restro-green hover:text-white rounded-xl"
              >
                Add Room Number
              </button>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {rooms.map((room) => (
                <div
                  key={room.id}
                  className="bg-white dark:bg-zinc-900 p-5 rounded-3xl border border-gray-150 dark:border-zinc-850 flex flex-col justify-between hover:shadow-md transition duration-300"
                >
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 rounded-2xl flex items-center justify-center bg-gray-50 dark:bg-zinc-800 text-gray-400">
                      <IconBed size={22} />
                    </div>
                    <div>
                      <h4 className="font-bold text-gray-900 dark:text-white text-lg">Room {room.name}</h4>
                      <p className="text-xs text-gray-400 font-medium uppercase mt-0.5">{room.room_type_name}</p>
                    </div>
                    <div className="flex items-center gap-1.5 ml-auto">
                      <button
                        onClick={() => handleShowUpdateRoom(room)}
                        className="w-8 h-8 rounded-full flex items-center justify-center transition hover:bg-gray-100 dark:hover:bg-zinc-800 text-gray-500"
                      >
                        <IconPencil size={18} stroke={iconStroke} />
                      </button>
                      <button
                        onClick={() => handleOpenICalModal(room)}
                        className="w-8 h-8 rounded-full flex items-center justify-center transition hover:bg-gray-100 dark:hover:bg-zinc-800 text-blue-500"
                        title="Channel Manager (iCal)"
                      >
                        <IconCalendarEvent size={18} stroke={iconStroke} />
                      </button>
                      <button
                        onClick={() => handleDeleteRoom(room.id)}
                        className="w-8 h-8 rounded-full flex items-center justify-center text-red-500 transition hover:bg-red-50 dark:hover:bg-red-950/20"
                      >
                        <IconTrash size={18} stroke={iconStroke} />
                      </button>
                    </div>
                  </div>

                  <div className="mt-6">
                    <button
                      onClick={() => handleDownloadRoomQR(room.name)}
                      className="w-full py-2.5 rounded-xl border border-gray-200 dark:border-zinc-800 text-xs font-bold hover:bg-gray-50 dark:hover:bg-zinc-800 flex items-center justify-center gap-2 transition"
                    >
                      <IconQrcode size={16} stroke={iconStroke} /> Download Menu Scanner
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      ) : (
        <div>
          {roomTypes.length === 0 ? (
            <div className="text-center py-20 bg-white dark:bg-zinc-900 border border-dashed rounded-3xl border-gray-200 dark:border-zinc-800">
              <IconCategory size={48} className="mx-auto text-gray-300 mb-4" />
              <h3 className="text-lg font-semibold">No Categories Configured</h3>
              <p className="text-sm text-gray-400 mt-1 mb-6">Create room categories (e.g. Standard, Deluxe) with overnight pricing rates.</p>
              <button
                onClick={() => document.getElementById("modal-add-type").showModal()}
                className="btn btn-outline border-restro-green text-restro-green hover:bg-restro-green hover:text-white rounded-xl"
              >
                Add Room Category
              </button>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {roomTypes.map((type) => (
                <div
                  key={type.id}
                  className="bg-white dark:bg-zinc-900 p-5 rounded-3xl border border-gray-150 dark:border-zinc-850 flex flex-col justify-between hover:shadow-md transition duration-300"
                >
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 rounded-2xl flex items-center justify-center bg-gray-50 dark:bg-zinc-800 text-gray-400">
                      <IconCategory size={22} />
                    </div>
                    <div>
                      <h4 className="font-bold text-gray-900 dark:text-white text-lg">{type.name}</h4>
                      <p className="text-sm text-restro-green font-extrabold mt-0.5">Rs. {type.rate} / Night</p>
                    </div>
                    <div className="flex items-center gap-1.5 ml-auto">
                      <button
                        onClick={() => handleShowUpdateRoomType(type)}
                        className="w-8 h-8 rounded-full flex items-center justify-center transition hover:bg-gray-100 dark:hover:bg-zinc-800 text-gray-500"
                      >
                        <IconPencil size={18} stroke={iconStroke} />
                      </button>
                      <button
                        onClick={() => handleDeleteRoomType(type.id)}
                        className="w-8 h-8 rounded-full flex items-center justify-center text-red-500 transition hover:bg-red-50 dark:hover:bg-red-950/20"
                      >
                        <IconTrash size={18} stroke={iconStroke} />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* --- ADD ROOM CATEGORY MODAL --- */}
      <dialog id="modal-add-type" className="modal modal-bottom sm:modal-middle">
        <div className="modal-box border border-restro-border-green dark:rounded-2xl bg-white dark:bg-zinc-950 p-6">
          <h3 className="font-bold text-lg text-gray-900 dark:text-white mb-4">Add Room Category</h3>
          <form onSubmit={handleAddRoomType} className="space-y-4">
            <div className="flex flex-col gap-1.5">
              <label className="text-xs font-bold uppercase tracking-wider text-gray-400">Category Name</label>
              <input
                ref={roomTypeNameRef}
                type="text"
                required
                className="p-3.5 rounded-xl border border-gray-200 dark:border-zinc-800 bg-transparent text-sm focus:outline-none focus:ring-1 focus:ring-restro-green"
                placeholder="e.g. Deluxe Suite"
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <label className="text-xs font-bold uppercase tracking-wider text-gray-400">Rate per Night (Rs.)</label>
              <input
                ref={roomTypeRateRef}
                type="number"
                required
                min="0"
                className="p-3.5 rounded-xl border border-gray-200 dark:border-zinc-800 bg-transparent text-sm focus:outline-none focus:ring-1 focus:ring-restro-green"
                placeholder="e.g. 5000"
              />
            </div>
            <div className="modal-action flex justify-end gap-3 pt-4">
              <button
                type="button"
                onClick={() => document.getElementById("modal-add-type").close()}
                className="px-5 py-3 rounded-xl font-bold bg-gray-100 hover:bg-gray-250 dark:bg-zinc-900 dark:hover:bg-zinc-800 text-sm transition"
              >
                Close
              </button>
              <button
                type="submit"
                className="px-5 py-3 rounded-xl font-bold bg-restro-green hover:bg-restro-green-button-hover text-white text-sm transition"
              >
                Save Category
              </button>
            </div>
          </form>
        </div>
      </dialog>

      {/* --- UPDATE ROOM CATEGORY MODAL --- */}
      <dialog id="modal-update-type" className="modal modal-bottom sm:modal-middle">
        <div className="modal-box border border-restro-border-green dark:rounded-2xl bg-white dark:bg-zinc-950 p-6">
          <h3 className="font-bold text-lg text-gray-900 dark:text-white mb-4">Edit Room Category</h3>
          <form onSubmit={handleUpdateRoomType} className="space-y-4">
            <input type="hidden" ref={roomTypeIdRef} />
            <div className="flex flex-col gap-1.5">
              <label className="text-xs font-bold uppercase tracking-wider text-gray-400">Category Name</label>
              <input
                ref={roomTypeNameUpdateRef}
                type="text"
                required
                className="p-3.5 rounded-xl border border-gray-200 dark:border-zinc-800 bg-transparent text-sm focus:outline-none focus:ring-1 focus:ring-restro-green"
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <label className="text-xs font-bold uppercase tracking-wider text-gray-400">Rate per Night (Rs.)</label>
              <input
                ref={roomTypeRateUpdateRef}
                type="number"
                required
                min="0"
                className="p-3.5 rounded-xl border border-gray-200 dark:border-zinc-800 bg-transparent text-sm focus:outline-none focus:ring-1 focus:ring-restro-green"
              />
            </div>
            <div className="modal-action flex justify-end gap-3 pt-4">
              <button
                type="button"
                onClick={() => document.getElementById("modal-update-type").close()}
                className="px-5 py-3 rounded-xl font-bold bg-gray-100 hover:bg-gray-250 dark:bg-zinc-900 dark:hover:bg-zinc-800 text-sm transition"
              >
                Close
              </button>
              <button
                type="submit"
                className="px-5 py-3 rounded-xl font-bold bg-restro-green hover:bg-restro-green-button-hover text-white text-sm transition"
              >
                Update Category
              </button>
            </div>
          </form>
        </div>
      </dialog>

      {/* --- ADD ROOM MODAL --- */}
      <dialog id="modal-add-room" className="modal modal-bottom sm:modal-middle">
        <div className="modal-box border border-restro-border-green dark:rounded-2xl bg-white dark:bg-zinc-950 p-6">
          <h3 className="font-bold text-lg text-gray-900 dark:text-white mb-4">Add Room Number</h3>
          <form onSubmit={handleAddRoom} className="space-y-4">
            <div className="flex flex-col gap-1.5">
              <label className="text-xs font-bold uppercase tracking-wider text-gray-400">Room Number / Name</label>
              <input
                ref={roomNameRef}
                type="text"
                required
                className="p-3.5 rounded-xl border border-gray-200 dark:border-zinc-800 bg-transparent text-sm focus:outline-none focus:ring-1 focus:ring-restro-green"
                placeholder="e.g. 101"
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <label className="text-xs font-bold uppercase tracking-wider text-gray-400">Room Category</label>
              <select
                ref={roomTypeIdSelectRef}
                required
                className="p-3.5 rounded-xl border border-gray-200 dark:border-zinc-800 bg-white dark:bg-zinc-900 text-sm focus:outline-none focus:ring-1 focus:ring-restro-green"
              >
                <option value="">Select a Category</option>
                {roomTypes.map((type) => (
                  <option key={type.id} value={type.id}>
                    {type.name} (Rs. {type.rate})
                  </option>
                ))}
              </select>
            </div>
            <div className="flex flex-col gap-1.5">
              <label className="text-xs font-bold uppercase tracking-wider text-gray-400">Google Drive Image Link 1 (Optional)</label>
              <input
                ref={roomImage1Ref}
                type="url"
                className="p-3.5 rounded-xl border border-gray-200 dark:border-zinc-800 bg-transparent text-sm focus:outline-none focus:ring-1 focus:ring-restro-green"
                placeholder="https://drive.google.com/..."
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <label className="text-xs font-bold uppercase tracking-wider text-gray-400">Google Drive Image Link 2 (Optional)</label>
              <input
                ref={roomImage2Ref}
                type="url"
                className="p-3.5 rounded-xl border border-gray-200 dark:border-zinc-800 bg-transparent text-sm focus:outline-none focus:ring-1 focus:ring-restro-green"
                placeholder="https://drive.google.com/..."
              />
            </div>
            <div className="modal-action flex justify-end gap-3 pt-4">
              <button
                type="button"
                onClick={() => document.getElementById("modal-add-room").close()}
                className="px-5 py-3 rounded-xl font-bold bg-gray-100 hover:bg-gray-250 dark:bg-zinc-900 dark:hover:bg-zinc-800 text-sm transition"
              >
                Close
              </button>
              <button
                type="submit"
                className="px-5 py-3 rounded-xl font-bold bg-restro-green hover:bg-restro-green-button-hover text-white text-sm transition"
              >
                Save Room
              </button>
            </div>
          </form>
        </div>
      </dialog>

      {/* --- UPDATE ROOM MODAL --- */}
      <dialog id="modal-update-room" className="modal modal-bottom sm:modal-middle">
        <div className="modal-box border border-restro-border-green dark:rounded-2xl bg-white dark:bg-zinc-950 p-6">
          <h3 className="font-bold text-lg text-gray-900 dark:text-white mb-4">Edit Room Number</h3>
          <form onSubmit={handleUpdateRoom} className="space-y-4">
            <input type="hidden" ref={roomIdRef} />
            <div className="flex flex-col gap-1.5">
              <label className="text-xs font-bold uppercase tracking-wider text-gray-400">Room Number / Name</label>
              <input
                ref={roomNameUpdateRef}
                type="text"
                required
                className="p-3.5 rounded-xl border border-gray-200 dark:border-zinc-800 bg-transparent text-sm focus:outline-none focus:ring-1 focus:ring-restro-green"
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <label className="text-xs font-bold uppercase tracking-wider text-gray-400">Room Category</label>
              <select
                ref={roomTypeIdUpdateSelectRef}
                required
                className="p-3.5 rounded-xl border border-gray-200 dark:border-zinc-800 bg-white dark:bg-zinc-900 text-sm focus:outline-none focus:ring-1 focus:ring-restro-green"
              >
                {roomTypes.map((type) => (
                  <option key={type.id} value={type.id}>
                    {type.name} (Rs. {type.rate})
                  </option>
                ))}
              </select>
            </div>
            <div className="flex flex-col gap-1.5">
              <label className="text-xs font-bold uppercase tracking-wider text-gray-400">Google Drive Image Link 1 (Optional)</label>
              <input
                ref={roomImage1UpdateRef}
                type="url"
                className="p-3.5 rounded-xl border border-gray-200 dark:border-zinc-800 bg-transparent text-sm focus:outline-none focus:ring-1 focus:ring-restro-green"
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <label className="text-xs font-bold uppercase tracking-wider text-gray-400">Google Drive Image Link 2 (Optional)</label>
              <input
                ref={roomImage2UpdateRef}
                type="url"
                className="p-3.5 rounded-xl border border-gray-200 dark:border-zinc-800 bg-transparent text-sm focus:outline-none focus:ring-1 focus:ring-restro-green"
              />
            </div>
            <div className="modal-action flex justify-end gap-3 pt-4">
              <button
                type="button"
                onClick={() => document.getElementById("modal-update-room").close()}
                className="px-5 py-3 rounded-xl font-bold bg-gray-100 hover:bg-gray-250 dark:bg-zinc-900 dark:hover:bg-zinc-800 text-sm transition"
              >
                Close
              </button>
              <button
                type="submit"
                className="px-5 py-3 rounded-xl font-bold bg-restro-green hover:bg-restro-green-button-hover text-white text-sm transition"
              >
                Update Room
              </button>
            </div>
          </form>
        </div>
      </dialog>

      {/* --- ICAL MANAGER MODAL --- */}
      <dialog id="modal-ical" className="modal modal-bottom sm:modal-middle">
        <div className="modal-box border border-restro-border-green dark:rounded-2xl bg-white dark:bg-zinc-950 p-6 max-w-2xl">
          <h3 className="font-bold text-lg text-gray-900 dark:text-white mb-2">
            Channel Manager: Room {selectedRoomForICal?.name}
          </h3>
          <p className="text-sm text-gray-500 mb-6">
            Sync bookings with Airbnb, Booking.com, and other platforms using iCal.
          </p>

          <div className="space-y-6">
            {/* Export Section */}
            <div className="p-4 bg-blue-50 dark:bg-blue-900/20 rounded-xl border border-blue-100 dark:border-blue-900/30">
              <h4 className="font-semibold text-blue-900 dark:text-blue-100 mb-2 flex items-center gap-2">
                <IconLink size={18} /> Export Calendar URL
              </h4>
              <p className="text-xs text-blue-700 dark:text-blue-300 mb-3">
                Paste this URL into Airbnb/Booking.com so they know when this room is booked locally.
              </p>
              <div className="flex gap-2">
                <input 
                  type="text" 
                  readOnly 
                  value={storeTenantId && selectedRoomForICal ? `${window.location.origin}/api/v1/rooms/ical/export/${storeTenantId}/${selectedRoomForICal.id}` : ''}
                  className="flex-1 p-2.5 text-xs rounded-lg border border-blue-200 dark:border-blue-800 bg-white dark:bg-zinc-900 focus:outline-none"
                />
                <button 
                  onClick={() => copyToClipboard(`${window.location.origin}/api/v1/rooms/ical/export/${storeTenantId}/${selectedRoomForICal.id}`)}
                  className="px-4 bg-blue-600 hover:bg-blue-700 text-white rounded-lg flex items-center gap-2 text-sm font-medium transition"
                >
                  <IconCopy size={16} /> Copy
                </button>
              </div>
            </div>

            {/* Import Section */}
            <div>
              <h4 className="font-semibold text-gray-900 dark:text-white mb-4">Import External Calendars</h4>
              
              <form onSubmit={handleAddICalLink} className="flex gap-3 mb-4">
                <input
                  ref={icalChannelNameRef}
                  type="text"
                  required
                  placeholder="Channel (e.g. Airbnb)"
                  className="w-1/3 p-2.5 text-sm rounded-lg border border-gray-200 dark:border-zinc-800 bg-transparent focus:outline-none"
                />
                <input
                  ref={icalUrlRef}
                  type="url"
                  required
                  placeholder="Paste iCal URL (.ics)"
                  className="flex-1 p-2.5 text-sm rounded-lg border border-gray-200 dark:border-zinc-800 bg-transparent focus:outline-none"
                />
                <button type="submit" className="px-4 bg-restro-green text-white font-medium text-sm rounded-lg hover:bg-restro-green-button-hover transition">
                  Add Sync
                </button>
              </form>

              {/* List of Links */}
              <div className="border border-gray-200 dark:border-zinc-800 rounded-xl overflow-hidden">
                {isICalLoading ? (
                  <div className="p-4 text-center text-sm text-gray-500">Loading...</div>
                ) : roomICalLinks.length === 0 ? (
                  <div className="p-4 text-center text-sm text-gray-500">No external calendars synced yet.</div>
                ) : (
                  <table className="w-full text-sm text-left">
                    <thead className="bg-gray-50 dark:bg-zinc-900 text-gray-500 dark:text-gray-400">
                      <tr>
                        <th className="px-4 py-2 font-medium">Channel</th>
                        <th className="px-4 py-2 font-medium">Last Synced</th>
                        <th className="px-4 py-2 font-medium text-right">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100 dark:divide-zinc-800">
                      {roomICalLinks.map((link) => (
                        <tr key={link.id}>
                          <td className="px-4 py-3 font-medium">{link.channel_name}</td>
                          <td className="px-4 py-3 text-gray-500">
                            {link.last_synced ? new Date(link.last_synced).toLocaleString() : 'Never'}
                          </td>
                          <td className="px-4 py-3 text-right">
                            <button 
                              onClick={() => handleDeleteICalLink(link.id)}
                              className="text-red-500 hover:text-red-700 p-1"
                            >
                              <IconTrash size={16} />
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            </div>
          </div>

          <div className="modal-action">
            <button
              type="button"
              onClick={() => document.getElementById("modal-ical").close()}
              className="px-5 py-3 rounded-xl font-bold bg-gray-100 hover:bg-gray-250 dark:bg-zinc-900 dark:hover:bg-zinc-800 text-sm transition"
            >
              Close
            </button>
          </div>
        </div>
      </dialog>
    </Page>
  );
}
